import React, { useState } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import apiClient from '../services/apiClient';

export const MarkFulfilledModal = ({
  isOpen,
  onClose,
  contentType, // 'tuition' or 'job'
  contentId,
  contentTitle,
  token,
  onSuccess
}) => {
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!remarks.trim()) {
      toast.error('Please provide remarks before marking as fulfilled');
      return;
    }

    setLoading(true);
    try {
      const endpoint = contentType === 'tuition'
        ? `/tuition-posts/${contentId}/mark-fulfilled`
        : `/jobs/${contentId}/mark-fulfilled`;

      await apiClient.post(
        `${endpoint}`,
        null,
        { params: { remarks: remarks.trim() } }
      );

      toast.success(`${contentType === 'tuition' ? 'Tuition' : 'Job'} marked as fulfilled successfully!`);
      onSuccess && onSuccess();
      onClose();
      setRemarks('');
    } catch (error) {
      console.error('Error marking as fulfilled:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as fulfilled');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
            Mark as Fulfilled
          </DialogTitle>
          <DialogDescription>
            You're about to mark "{contentTitle}" as fulfilled. This will notify the admin and the post will be removed from active listings.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Please provide details about how this {contentType === 'tuition' ? 'tuition requirement was fulfilled' : 'position was filled'}.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="remarks">Remarks / Comments *</Label>
            <Textarea
              id="remarks"
              placeholder={contentType === 'tuition'
                ? "e.g., Found an excellent tutor through the platform. Classes started on..."
                : "e.g., Successfully hired a qualified candidate. Position filled on..."
              }
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-gray-500">
              This information helps us improve our service
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !remarks.trim()}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Marking...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark as Fulfilled
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarkFulfilledModal;
